import * as React from 'react';
import { useLayoutEffect, useMemo, useState } from 'react';
import { FormattedMessage, injectIntl, WrappedComponentProps as InjectedIntlProps } from 'react-intl';
import { Loader } from 'semantic-ui-react';
import { matchPath } from 'react-router-dom';

import { RowColor } from '../../../components/Table/Table';
import TablePagination from '../../../components/Table/TablePagination';
import { localeData } from '../../../locales/data';
import { DatasetField, DatasetFieldsResponse } from '../../../shared/api/api';
import { EnhancedQueryResult, SEARCH_RESULTS_LIMIT } from '../../../shared/api/apiHooks';

import { FilterPaginationResult, useFilterSortOrder } from '../Filters/auxiliaryFields';
import { getColumns } from './AllFieldsListTableColumns';
import { getColumnTransform } from '../DataTableCommon/DataTableCommon';
import { DATA_FIELD_PATH, getDataFieldPath, getSingleDatasetPath } from '../dataExplorerRouterPaths';
import { stringifyFilters, toRequestQuery } from '../Filters/queryTransformers';
import { DatasetsFiltersTransformers } from '../DatasetFilters/datasetFilterConfig';
import { addModifiers } from '../../../shared/helpers';

import './AllFieldsListTable.scss';
import isEqual from 'lodash/isEqual';
import { useDataExplorerTrackCustomEvent } from '../Filters/dataExplorerAnalytics';
import { FiltersControl } from '../Filters/types';
import analytics from '../../../shared/services/analytics/analytics';
import { useDataExplorerHistory } from '../dataExplorerHistoryContext';
import { getCoreRowModel, getExpandedRowModel, getGroupedRowModel, Row, SortingState, useReactTable } from '@tanstack/react-table';
import { DATASET_NAME_TABLE_COLUMN } from '../DatasetFieldsListTable/DatasetFieldsListTableColumns';
import { DataExplorerResultTableMarkup } from '../ResultTableMarkup/ResultTableMarkup';
import { FormattedMessageHelper } from '../../../shared/helpers/internationalization';

type Props = {
  result: EnhancedQueryResult<DatasetFieldsResponse, Error>;
  control: FiltersControl;
  filterPagination: FilterPaginationResult;
  isSearchActive: boolean;
  isPyramidMultiplierVisible: boolean;
} & InjectedIntlProps;

const GROUP_SIZE = 3;

function getResultNumber(page: number, pageSize: number, indexOnPage: number) {
  return (page * pageSize + indexOnPage).toString();
}
const grouping = [DATASET_NAME_TABLE_COLUMN];

const AllFieldListTableWithoutIntl = ({ intl, result, control, filterPagination, isSearchActive, isPyramidMultiplierVisible }: Props) => {
  const history = useDataExplorerHistory();

  const { isFetching, isError, data, isSearchLimitExceeded } = result;
  const { sortOrder, handleOrderChange, isOrderDisabled } = useFilterSortOrder({ control, isSearchActive });

  const transformColumn = React.useMemo(
    () => getColumnTransform<DatasetField>({ intl, sortOrder, handleOrderChange, isOrderDisabled }),
    [intl, sortOrder, isOrderDisabled]
  );
  const trackEvent = useDataExplorerTrackCustomEvent();

  const tableColumns = React.useMemo(() => {
    const columns = getColumns(trackEvent, isPyramidMultiplierVisible);

    // @ts-ignore
    return columns.map(transformColumn);
  }, [transformColumn, trackEvent, isPyramidMultiplierVisible]);

  const { page, handlePageChange, handlePageSizeChange, pageSize } = filterPagination;
  const getTrProps = React.useCallback(
    (state: unknown, rowInfo?: Row<DatasetField>, column?: undefined) => {
      return {
        // @ts-ignore
        ...RowColor(state, rowInfo, column),
        onClickCapture: (ev: React.MouseEvent<HTMLElement>) => {
          const target = ev.target as HTMLElement;
          const tagName = target.tagName;

          if (tagName === 'A' && isSearchActive) {
            const isField = matchPath((target as HTMLAnchorElement | null)?.pathname ?? '', { path: DATA_FIELD_PATH, exact: true });

            const event = isField ? 'de_click_field_result' : 'de_click_dataset_result';
            const indexOnPage = (isField ? rowInfo?.index : Number(rowInfo?.index)) ?? 0;
            analytics.trackCustomEvent(event, {
              event_label: getResultNumber(page, pageSize, indexOnPage),
            });
          }

          if (tagName === 'A' || tagName === 'BUTTON') {
            return;
          }
          // @ts-ignore
          const original = rowInfo?.original || rowInfo?.subRows[0]._original;
          const fieldId = original.id;
          const datasetId = original.dataset.id;

          const subRowsCount = rowInfo?.subRows?.length ?? 0;
          if (subRowsCount > 1) {
            //   this is aggregated row, click on it will redirect to dataset
            const query =
              subRowsCount < GROUP_SIZE ? undefined : stringifyFilters(toRequestQuery(control._formValues, DatasetsFiltersTransformers));
            const datasetId = original.dataset.id;
            history.push(getSingleDatasetPath({ datasetId, query }));
            trackEvent('de_click_dataset');
            return;
          }

          history.push(getDataFieldPath({ fieldId, datasetId }));
          trackEvent('de_click_field');
        },
      };
    },
    [history, control, trackEvent, pageSize, page]
  );

  const fields = data?.results ?? [];

  const expanded = fields.reduce((acc, item, i) => {
    // assign object instead of true because React Table throws error when we click on pivoted row
    acc[`${DATASET_NAME_TABLE_COLUMN}:${item.dataset.name}`] = true;
    return acc;
  }, {} as { [key: string]: boolean });

  useLayoutEffect(() => {
    // adjust width for all rows in first column to equal first header column width
    const firstHeaderSize = document.querySelector('.rt-thead .rt-th:nth-child(1)')?.getBoundingClientRect();
    if (firstHeaderSize) {
      document.documentElement.style.setProperty('--allFieldsFirstHeaderWidth', firstHeaderSize.width + 'px');
    }
  }, [fields]);

  // Calculate effective count for pagination when search is active
  const effectiveCount = isSearchActive ? Math.min(data?.count ?? 0, SEARCH_RESULTS_LIMIT) : data?.count ?? 0;
  const totalPages = Math.ceil(effectiveCount / pageSize);
  const isSearchLimitWarningShown = isSearchActive && (data?.count ?? 0) > SEARCH_RESULTS_LIMIT;

  const Pagination = useMemo(
    () =>
      TablePagination({
        hasButtons: false,
        numOfResults: effectiveCount,
        pageSizeLimit: 50,
        pageSize: pageSize,
        page: page,
        pages: totalPages,
        onPageChange: handlePageChange,
        onPageSizeChange: handlePageSizeChange,
        isSearchLimitWarningShown: isSearchLimitWarningShown,
      }),
    [effectiveCount, page, pageSize, totalPages, isSearchLimitWarningShown]
  );

  const [sorting, setSorting] = useState<SortingState>([]);

  const table = useReactTable({
    columns: tableColumns,
    data: data?.results || [],
    state: {
      grouping,
      expanded,
    },
    onSortingChange: setSorting,
    getCoreRowModel: getCoreRowModel(),
    enableSortingRemoval: false,
    getGroupedRowModel: getGroupedRowModel(),
    getExpandedRowModel: getExpandedRowModel(),
    manualSorting: true,
  });

  const headerGroups = table.getHeaderGroups();
  const isDataLoaded = !!data;

  // show loader when data load for the first time
  if (isFetching && !data) {
    return (
      <div className="loader">
        <Loader active={true} inline="centered" size="massive" />
      </div>
    );
  }

  if (isSearchLimitExceeded) {
    return (
      <div className="agreements__content full-height">
        <div className="agreements__content-scroll full-height">
          <div className="agreements__not-available full-height">
            <FormattedMessage id="dataExplorer.searchLimitError" />
          </div>
        </div>
      </div>
    );
  }

  if (isError || !data) {
    return (
      <div className="agreements__content full-height">
        <div className="agreements__content-scroll full-height">
          <div className="agreements__not-available full-height">
            <FormattedMessage id="dataExplorer.datafields.error" defaultMessage={localeData.en['dataExplorer.datafields.error']} />
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className={addModifiers('data-table__container', ['search-fields'])}>
      <DataExplorerResultTableMarkup<DatasetField>
        isDataLoaded={isDataLoaded}
        isFetching={isFetching}
        headerGroups={headerGroups}
        sorting={sorting}
        table={table}
        getTrProps={getTrProps}
        Pagination={Pagination}
        noDataText={<FormattedMessageHelper id={'dataExplorer.noFields'} />}
      />
    </div>
  );
};

export const AllFieldsListTable = injectIntl(
  React.memo(AllFieldListTableWithoutIntl, (prevProps, nextProps) => isEqual(prevProps, nextProps))
);
